import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Session } from '@supabase/supabase-js';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from '@/lib/supabase';
import { identifyUser, resetUser } from '@/lib/monitoring';

export type AuthProfile = {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
  role: 'member' | 'moderator' | 'admin';
};

type AuthState = {
  session: Session | null;
  profile: AuthProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  user: Session['user'] | null;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

/* ================================================================
 * AuthProvider — mounts once at the top of the app tree.
 * All components read from the same shared state via useAuth().
 * ================================================================ */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loadingSession, setLoadingSession] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    let mounted = true;

    // 1. Hydrate from stored session immediately
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoadingSession(false);
      if (data.session?.user) identifyUser(data.session.user.id);
    });

    // 2. Keep in sync with auth events
    const { data: sub } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!mounted) return;

      // USER_UPDATED fires with null session during the token rotation cycle
      // that follows supabase.auth.updateUser(). Ignore it so we don't
      // briefly appear logged-out.
      if (event === 'USER_UPDATED' && !nextSession) return;

      setSession(nextSession);

      if (nextSession?.user) {
        identifyUser(nextSession.user.id);
      } else if (event === 'SIGNED_OUT') {
        resetUser();
        queryClient.removeQueries({ queryKey: ['profile'] });
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [queryClient]);

  // Profile — fetched once, cached 5 min
  const profileQuery = useQuery<AuthProfile | null>({
    queryKey: ['profile', session?.user?.id],
    enabled: !!session?.user,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, display_name, avatar_url, role')
        .eq('id', session!.user.id)
        .single();
      if (error) throw error;
      return data;
    },
  });

  // isLoading stays true until:
  //  - initial session has been read from storage, AND
  //  - if there IS a session, the profile has been fetched
  const profileFetching =
    !!session?.user &&
    profileQuery.fetchStatus === 'fetching' &&
    profileQuery.data === undefined;

  const isLoading = loadingSession || profileFetching;

  const value: AuthState = {
    session,
    user: session?.user ?? null,
    profile: profileQuery.data ?? null,
    isLoading,
    isAuthenticated: !!session,
    isAdmin: profileQuery.data?.role === 'admin',
    signOut: async () => {
      await supabase.auth.signOut();
    },
  };

  // Debug: log auth state changes in development
  if (import.meta.env.DEV) {
    console.log('[Auth]', {
      userId: session?.user?.id?.slice(0, 8),
      role: profileQuery.data?.role,
      isAdmin: profileQuery.data?.role === 'admin',
      isLoading,
    });
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/* ================================================================
 * useAuth — reads the shared singleton state.
 * Never creates its own subscriptions or state.
 * ================================================================ */
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
